import React from 'react';
import { injectIntl } from 'gatsby-plugin-intl';
import styled from 'styled-components';
import { device } from "./device";

import H3 from './styledComponents/H3';
import Paragraph from './styledComponents/Paragraph';
import RuleDescription from './styledComponents/RuleDescription';
import ChapterSpace from './styledComponents/ChapterSpace';

import { Container,Cell,Grid,AnchorBlock,ChapterHeader,ColorItem,ParentAnchor } from './blockComponents'
import { PlainImage } from './imageComponents'

import { useStaticQuery, graphql } from "gatsby"
import Img from "gatsby-image"

const ColorGroup = styled.div`
    margin-bottom: 48px;
`;
const ColorName = styled.h4`
    margin: 0 0 16px;

    font-size: 24px;
    font-weight: 400;
    line-height: 1.2;

    @media ${device.lg} {
        font-size: 32px;
        margin: 0 0 24px;
    }
`;

function Color({ 
    intl,
    activeAnchor,
    setActiveAnchor,
    pageHeight,
    refs
    }) {

    const { mobileImage, desktopImage } = useStaticQuery(
        graphql`
            query {
                mobileImage: file(relativePath: { eq: "color/color-proportion-mob@2x.png" }) {
                    childImageSharp {
                        fluid(maxWidth : 992) {
                            ...GatsbyImageSharpFluid_noBase64
                        }
                    }
                  }
                  desktopImage: file(relativePath: { eq: "color/color-proportion@2x.png" }) {
                    childImageSharp {
                        fluid(maxWidth : 992) {
                            ...GatsbyImageSharpFluid_noBase64
                        }
                    }
                  }
            }
        `
        )

    const sources = [
        mobileImage.childImageSharp.fluid,
        {
        ...desktopImage.childImageSharp.fluid,
        media: `(min-width: 576px)`,
        },
    ]
        
    return (
        <ParentAnchor
            key='colorSection'
            blockId="color-section"
            blockName='colorSection'
            refs={refs}>
            <ChapterHeader title={intl.formatMessage({ id: 'MainMenu_2' })}></ChapterHeader>
            <Container>
                <Grid>
                    <Cell col={12} colXl={10} colXxl={8} leftXl={2} leftXxl={3}>
                    <AnchorBlock
                            key='colorProportion'
                            blockName='colorProportion'
                            blockId='color-proportion'
                            activeAnchor={activeAnchor}
                            setActiveAnchor={setActiveAnchor}
                            pageHeight={pageHeight}
                            refs={refs}>
                            <H3 space="TopDown">{intl.formatMessage({ id: 'color_proportions' })}</H3>
                            <Img fluid={sources} />
                        </AnchorBlock>

                        <AnchorBlock
                            key='colorMain'
                            blockName='colorMain'
                            blockId='color-main'
                            activeAnchor={activeAnchor}
                            setActiveAnchor={setActiveAnchor}
                            pageHeight={pageHeight}
                            refs={refs}>
                            <H3 space="TopDown">{intl.formatMessage({ id: 'color_title' })}</H3>
                            <Grid>
                                <Cell col={6} colMd={3}>
                                    <ColorGroup>
                                        <ColorName>Light</ColorName>
                                        <ColorItem background="#f7f7f7" number="1" hex="#F7F7F7" />
                                        <ColorItem background="#EDEDED" number="2" hex="#EDEDED" />
                                        <ColorItem background="#DBDBDB" number="3" hex="#DBDBDB" />
                                        <ColorItem background="#C7C7C7" number="4" hex="#C7C7C7" />
                                    </ColorGroup>
                                </Cell>
                                <Cell col={6} colMd={3}>
                                    <ColorGroup>
                                        <ColorName>Middle</ColorName>
                                        <ColorItem background="#ADADAD" number="1" hex="#ADADAD" />
                                        <ColorItem background="#919191" number="2" hex="#919191" />
                                        <ColorItem background="#787878" number="3" hex="#787878" />
                                        <ColorItem background="#5E5E5E" number="4" hex="#5E5E5E" />
                                    </ColorGroup>
                                </Cell>
                                <Cell col={6} colMd={3}>
                                    <ColorGroup>
                                        <ColorName>Dark</ColorName>
                                        <ColorItem background="#3B3B3B" number="1" hex="#3B3B3B" />
                                        <ColorItem background="#303030" number="2" hex="#303030" />
                                        <ColorItem background="#242424" number="3" hex="#242424" />
                                        <ColorItem background="#181818" number="4" hex="#181818" />
                                    </ColorGroup>
                                </Cell>
                                <Cell col={6} colMd={3}>
                                    <ColorGroup>
                                        <ColorName>Green</ColorName>
                                        <ColorItem background="#5AE4AA" number="1" hex="#5AE4AA" />
                                        <ColorItem background="#1DCF8E" number="2" hex="#1DCF8E" />
                                        <ColorItem background="#00AD74" number="3" hex="#00AD74" />
                                    </ColorGroup>
                                </Cell>
                                <Cell col={12}>
                                    <ColorName>Black and white</ColorName>
                                </Cell>
                                <Cell col={6} colMd={3}>
                                    <ColorItem background="#FFFFFF" number="White" hex="#FFFFFF" />
                                </Cell>
                                <Cell col={6} colMd={3}>
                                    <ColorItem background="#000000" number="Black" hex="#000000" />
                                </Cell>
                            </Grid>
                        </AnchorBlock>
                        <AnchorBlock
                            key='colorAdditional'
                            blockName='colorAdditional'
                            blockId='color-additional'
                            activeAnchor={activeAnchor}
                            setActiveAnchor={setActiveAnchor}
                            pageHeight={pageHeight}
                            refs={refs}>
                            <H3 space="TopDown">{intl.formatMessage({ id: 'color_additional_title' })}</H3>
                            <Paragraph space="Down">{intl.formatMessage({ id: 'color_additional_text1' })}</Paragraph>
                            <Grid>
                                <Cell col={6} colMd={3}>
                                    <ColorGroup>
                                        <ColorName>Red</ColorName>
                                        <ColorItem background="#FF725C" number="1" hex="#FF725C" />
                                        <ColorItem background="#EA5949" number="2" hex="#EA5949" />
                                        <ColorItem background="#C9423B" number="3" hex="#C9423B" />
                                    </ColorGroup>
                                </Cell>
                                <Cell col={6} colMd={3}>
                                    <ColorGroup>
                                        <ColorName>Yellow</ColorName>
                                        <ColorItem background="#F8DF52" number="1" hex="#F8DF52" />
                                        <ColorItem background="#F5CA1B" number="2" hex="#F5CA1B" />
                                        <ColorItem background="#DAB00A" number="3" hex="#DAB00A" />
                                    </ColorGroup>
                                </Cell>
                                <Cell col={6} colMd={3}>
                                    <ColorGroup>
                                        <ColorName>Lime</ColorName>
                                        <ColorItem background="#ADF75E" number="1" hex="#ADF75E" />
                                        <ColorItem background="#97DE4B" number="2" hex="#97DE4B" />
                                        <ColorItem background="#80C23A" number="3" hex="#80C23A" />
                                    </ColorGroup>
                                </Cell>
                                <Cell col={6} colMd={3}>
                                    <ColorGroup>
                                        <ColorName>Blue</ColorName>
                                        <ColorItem background="#3AD4F2" number="1" hex="#3AD4F2" />
                                        <ColorItem background="#11B2DF" number="2" hex="#11B2DF" />
                                        <ColorItem background="#1083AD" number="3" hex="#1083AD" />
                                    </ColorGroup>
                                </Cell>
                            </Grid>
                            <div>
                                <RuleDescription rule="do">{intl.formatMessage({ id: 'color_example' })}</RuleDescription>
                                <PlainImage src="color/color-additional@2x.jpg" last="xs" alt="" />
                            </div>
                        </AnchorBlock>
                    </Cell>
                </Grid>
            </Container>
            <ChapterSpace />
        </ParentAnchor>
        
    )
}

export default injectIntl(Color)


